import { useEffect, useState } from "react";
import { ScipCategoryNode } from "../../../store/Models/Api/Surveys/ScipData";
import { TreeNode } from "../../../store/Models/Api/TreeList";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import DependentDropdowns from "../DependentDropdowns";
import { LoadingSpinner, LoadingSpinnerSize } from "../LoadingSpinner";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";

export enum CategoryType {
    Article,
    Mixture,
    Material
}

interface ScipCategoryProps {
    onListChange?: ((categoryIdentifiers: string[]) => void);
    localizer: IStringLocalizer;
    existingCategories: string[];
    categoryType: CategoryType;
    buttonText?: string;
    maxNumberOfAllowedElements?: number;
    editEnabled?: boolean; // defaults to true
} 

interface SelectedCategory {
    categoryFullName: string;
    identifier: string;
}

const ScipCategory: React.FC<ScipCategoryProps> = ({ onListChange, localizer, existingCategories, categoryType, buttonText, maxNumberOfAllowedElements, editEnabled }) => {
    const modalId = categoryType === CategoryType.Article ? "article-category-modal" :
        categoryType === CategoryType.Mixture ? "mixture-category-modal" : "material-category-modal" ;
    const [initialCategoriesLoaded, setInitialCategoriesLoaded] = useState(false);
    const [categoriesFullNames, setCategoriesFullNames] = useState({} as { [identifier: string]: string });
    const [newCategoryToAdd, setNewCategoryToAdd] = useState({} as SelectedCategory);
    const [categoriesToDisplay, setCategoriesToDisplay] = useState([] as SelectedCategory[]);
    const [displayAddButton, setDisplayAddButton] = useState(false);
    const [categoriesList, setCategoriesList] = useState<ScipCategoryNode>();
    const [resetToInitialNode, setResetToInitialNode] = useState(false);

    useEffect(() => {
        let doNotRender = false;
        const serviceClient = new ToxnotServiceClient();

        (async () => {
            const categoriesDescriptionsList = [] as { [codeId: number]: string };
            const result = await serviceClient.scipIuclidStaticDataGet();

            if (!result.success || result.data === null) {
                throw new Error("Scip data was not valid");
            }

            const categories = categoryType === CategoryType.Article ? result.data.articleCategoriesTreeRoot :
                categoryType === CategoryType.Mixture ? result.data.mixtureCategoriesTreeRoot : result.data.materialCategoriesTreeRoot;

            if (!doNotRender) {
                addCategoryTreeToIdTable(categories as TreeNode, categoriesDescriptionsList, '');
                setCategoriesToDisplay(existingCategories.map(c => mapCategory(c, categoriesDescriptionsList)));
                setCategoriesFullNames(categoriesDescriptionsList);
                setCategoriesList(categories);
                setInitialCategoriesLoaded(true);
            }
        })();

        $("#" + modalId).off("hidden.bs.modal").on("hidden.bs.modal", () => {
            clearData();
        });

        return () => {
            doNotRender = true;
        };
    }, []);

    const mapCategory = (identifier: string, categoriesDescriptionsList: { [identifier: string]: string }) => {
        return {
            identifier: identifier,
            categoryFullName: categoriesDescriptionsList[identifier]
        } as SelectedCategory;
    }

    const addCategoryTreeToIdTable = (treeNode: TreeNode, categoriesFullNames: { [identifier: string]: string }, prefix: string) => {
        prefix = prefix ?? "";

        const description = prefix + (prefix.length > 0 ? "; " : "") + localizeString(treeNode.userFacingDescription);
        categoriesFullNames[treeNode.identifier] = description;

        for (let childNode of treeNode.children) {
            addCategoryTreeToIdTable(childNode, categoriesFullNames, description);
        }
    }

    const openModal = () => {
        let currentModalId = "#" + modalId;
        $(currentModalId).modal('show');
        setResetToInitialNode(false);
    }

    const addCategory = () => {
        if (newCategoryToAdd) {
            var newSelectedCategoriesList = categoriesToDisplay.slice();
            newSelectedCategoriesList.push(newCategoryToAdd);
            setCategoriesToDisplay(newSelectedCategoriesList);
            onListChange?.(newSelectedCategoriesList.map(c => c.identifier));
        }
        clearData();

    }

    const removeCategory = (identifier: string) => {
        var newSelectedCategoriesList = categoriesToDisplay.slice().filter(c => c.identifier != identifier);
        setCategoriesToDisplay(newSelectedCategoriesList);
        onListChange?.(newSelectedCategoriesList.map(c => c.identifier));
    }

    const onAllDropdrownsSelected = (selectedNodes: TreeNode[]) => {
        var identifier = selectedNodes[selectedNodes.length - 1].identifier;
        setNewCategoryToAdd({
            categoryFullName: categoriesFullNames[identifier],
            identifier: identifier
        });
        setDisplayAddButton(true);
    }

    const onNotAllDropdrownsSelected = () => {
        setDisplayAddButton(false);
    }

    const clearData = () => {
        setResetToInitialNode(true);
    }

    function localizeString(stringText: string): string {
        const hyphenIndex = stringText.indexOf('-');
    
        // If there's no hyphen, localize the entire string
        if (hyphenIndex === -1) {
            return localizer.localizeString(stringText);
        }
    
        // Split the string into the part before and after the hyphen
        const beforeHyphen = stringText.substring(0, hyphenIndex).trim();
        const afterHyphen = stringText.substring(hyphenIndex + 1).trim();
    
        // Check if the part before the hyphen is a number
        if (/^\d+$/.test(beforeHyphen)) {
            // Only localize the part after the hyphen
            const localizedAfterHyphen = localizer.localizeString(afterHyphen);
            // Return the combined string with the number intact
            return `${beforeHyphen} - ${localizedAfterHyphen}`;
        } else {
            // If the part before the hyphen is not a number, localize the entire string
            return localizer.localizeString(stringText);
        }
    }   

    const editEnabledResolved = (editEnabled ?? true);

    return (
        <>
            {editEnabledResolved &&
                <>
                    {(maxNumberOfAllowedElements === null || maxNumberOfAllowedElements === undefined || maxNumberOfAllowedElements > categoriesToDisplay.length) &&
                        <button onClick={openModal} className="btn btn-default m-b-5">
                            {buttonText ? buttonText : <span className="glyphicon glyphicon-pencil"></span>}
                        </button>
                    }
                </>
            }

            <ToxnotReactModal modalId={modalId} localizedTitle={localizer.localizeString("Add Category")}>
                <ToxnotReactModalBody>
                    {categoriesList &&
                        <DependentDropdowns
                            initialNode={categoriesList}
                            localizer = {localizer}
                            onAllDropdrownsSelected={onAllDropdrownsSelected}
                            onNotAllDropdrownsSelected={onNotAllDropdrownsSelected}
                            resetToInitialNode={resetToInitialNode}
                            parentNodeName={localizer.localizeString("Add Category")}
                            childNodeName={localizer.localizeString("Add Sub-Category")}
                        ></DependentDropdowns>}
                </ToxnotReactModalBody>
                <ToxnotReactModalFooter localizer={localizer}>
                    {displayAddButton && <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal" onClick={addCategory}>{localizer.localizeString("Add Category")}</button>}
                </ToxnotReactModalFooter>
            </ToxnotReactModal>

            <div className="scip-article-category-list">
                {
                    !initialCategoriesLoaded && <LoadingSpinner size={LoadingSpinnerSize.Small} />
                }
                {
                    categoriesToDisplay.map((category, index) =>
                        <div className="scip-article-category" key={index}>
                            {category.categoryFullName} {editEnabledResolved && <span className="remove-scip-article-category-button" onClick={() => removeCategory(category.identifier)}>×</span>}
                        </div>
                    )
                }
            </div>
        </>
    );
};

export default ScipCategory;
